import React, {Component} from 'react';
import {
  Button, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, MenuItem, TextField, Typography
} from '@mui/material';
import {ExpandMore, ExpandLess} from '@mui/icons-material';

const TON_OPTIONS = [
  {value: 0, label: '0 - Unknown'},
  {value: 1, label: '1 - International'},
  {value: 2, label: '2 - National'},
  {value: 3, label: '3 - Network Specific'},
  {value: 5, label: '5 - Alphanumeric'},
  {value: 6, label: '6 - Abbreviated'}
];

const NPI_OPTIONS = [
  {value: 0, label: '0 - Unknown'},
  {value: 1, label: '1 - ISDN/E.164'},
  {value: 3, label: '3 - Data/X.121'},
  {value: 4, label: '4 - Telex'},
  {value: 8, label: '8 - National'},
  {value: 9, label: '9 - Private'},
  {value: 14, label: '14 - Internet (IP)'}
];

const SMPP_VERSION_OPTIONS = [
  {value: 34, label: 'SMPP 3.4'},
  {value: 50, label: 'SMPP 5.0'},
  {value: 33, label: 'SMPP 3.3'}
];

const SectionHeader = ({label, expanded, onClick}) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none',
      padding: '8px 0', marginTop: 4
    }}
  >
    <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1, color: 'text.secondary', fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5}}>
      {label}
    </Typography>
    {expanded ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
  </div>
);

class EditOperator extends Component {
  constructor(props) {
    super(props);
    const {data} = props;
    const protocol = data.protocol || 'smpp';
    // SMPP config is nested inside smppConfig from API response
    const cfg = data.smppConfig || data.smpp_config || {};
    this.state = {
      // Basic
      name: data.name || '',
      host: data.host || '',
      port: String(data.port || 2775),
      username: data.username || '',
      password: data.password || '',
      throughput: String(data.throughput || 0),
      maxPending: String(data.maxPending || data.max_pending || 5000),
      // Sessions: 0/absent is stored as 1 server-side, so read it the same way.
      instances: String(parseInt(data.instances, 10) || 1),
      isActive: data.isActive !== undefined ? data.isActive : (data.is_active !== false),
      // SMPP Protocol (from nested smppConfig)
      interfaceVersion: cfg.interfaceVersion || cfg.interface_version || 34,
      transceiverMode: cfg.transceiverMode !== undefined ? cfg.transceiverMode : (cfg.transceiver_mode !== undefined ? cfg.transceiver_mode : true),
      // Address TON/NPI
      sourceAddrTon: cfg.sourceAddrTon !== undefined ? cfg.sourceAddrTon : (cfg.source_addr_ton !== undefined ? cfg.source_addr_ton : 1),
      sourceAddrNpi: cfg.sourceAddrNpi !== undefined ? cfg.sourceAddrNpi : (cfg.source_addr_npi !== undefined ? cfg.source_addr_npi : 1),
      destAddrTon: cfg.destAddrTon !== undefined ? cfg.destAddrTon : (cfg.dest_addr_ton !== undefined ? cfg.dest_addr_ton : 1),
      destAddrNpi: cfg.destAddrNpi !== undefined ? cfg.destAddrNpi : (cfg.dest_addr_npi !== undefined ? cfg.dest_addr_npi : 1),
      // Timers
      reconnectDelay: String(cfg.reconnectDelay || cfg.reconnect_delay || 5),
      keepalive: String(cfg.keepalive || 60),
      enquireLinkInterval: String(cfg.enquireLinkInterval || cfg.enquire_link_interval || 30),
      waitAck: String(cfg.waitAck || cfg.wait_ack || 10),
      waitAckExpire: cfg.waitAckExpire || cfg.wait_ack_expire || '0x00',
      // DLT TM Chain
      dltTmId: data.dltTmId || data.dlt_tm_id || '',
      dltTmMode: data.dltTmMode || data.dlt_tm_mode || 'chain',
      // DLT TLV Tag Overrides (from smppConfig)
      dltEntityTag: cfg.dltEntityTag || cfg.dlt_entity_tag || '',
      dltTemplateTag: cfg.dltTemplateTag || cfg.dlt_template_tag || '',
      dltTmTag: cfg.dltTmTag || cfg.dlt_tm_tag || '',
      // DLR correlation
      msgIdType: cfg.msgIdType || cfg.msg_id_type || '',
      // Section toggle
      showSmppSection: false,
      showAddressSection: false,
      showTimerSection: false,
      showDltSection: false,
      // Track protocol for conditional rendering
      _protocol: protocol
    };
  }

  handleChange = (field) => (e) => {
    this.setState({[field]: e.target.value});
  }

  handleNumberChange = (field) => (e) => {
    this.setState({[field]: parseInt(e.target.value) || 0});
  }

  // Mirrors the server-side guard (operators.ValidateSessions): 1..8, and
  // more than one bind needs a per-bind throughput cap and submit window.
  sessionsError = () => {
    const {instances, throughput, maxPending} = this.state;
    const sessions = Number(instances);
    if (!Number.isInteger(sessions) || sessions < 1 || sessions > 8) return 'Sessions must be between 1 and 8';
    if (sessions > 1 && !(parseInt(throughput, 10) > 0)) return 'Sessions > 1 require a throughput cap';
    if (sessions > 1 && !(parseInt(maxPending, 10) > 0)) return 'Sessions > 1 require max_pending > 0';
    return null;
  }

  // TPS and Max Pending are PER SESSION and stored exactly as entered; the
  // backend never divides or rounds. The total is display-only.
  sessionsSummary = () => {
    const {instances, throughput, maxPending} = this.state;
    const n = parseInt(instances, 10) || 1;
    const tps = parseInt(throughput, 10) || 0;
    const pending = parseInt(maxPending, 10) || 0;
    if (n <= 1) return `1 session \u00b7 ${tps > 0 ? tps : 'unlimited'} TPS`;
    return `Total: ${n} sessions \u00d7 ${tps} TPS = ${n * tps} msg/s \u00b7 in-flight window ${n} \u00d7 ${pending} = ${n * pending}`;
  }

  handleSubmit = () => {
    const {name, host} = this.state;
    if (!name || !host) {
      alert('Name and Host are required');
      return;
    }
    const sessionsErr = this.state._protocol === 'smpp' ? this.sessionsError() : null;
    if (sessionsErr) {
      alert(sessionsErr);
      return;
    }
    // Exclude section toggle state and internal fields
    const {showSmppSection, showAddressSection, showTimerSection, showDltSection, _protocol, ...params} = this.state;
    this.props.onSubmit(params);
  }

  render() {
    const {data, onClose} = this.props;
    const {
      name, host, port, username, password, throughput, maxPending, instances, isActive,
      interfaceVersion, transceiverMode,
      sourceAddrTon, sourceAddrNpi, destAddrTon, destAddrNpi,
      reconnectDelay, keepalive, enquireLinkInterval, waitAck, waitAckExpire,
      showSmppSection, showAddressSection, showTimerSection, showDltSection,
      dltTmId, dltTmMode, dltEntityTag, dltTemplateTag, dltTmTag, msgIdType, _protocol
    } = this.state;

    return (
      <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Operator: {data.code}</DialogTitle>
        <DialogContent dividers>
          <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            {/* Read-only info */}
            <TextField label="Code" value={data.code} size="small" disabled />
            <TextField label="SMSC ID" value={data.smsc_id || data.smscId} size="small" disabled />
            <TextField label="Protocol" value={_protocol} size="small" disabled />

            {/* Basic editable fields */}
            <TextField label="Name" value={name} onChange={this.handleChange('name')} size="small" required />
            <div style={{display: 'flex', gap: 8}}>
              <TextField label="Host" value={host} onChange={this.handleChange('host')} size="small" required style={{flex: 2}} />
              <TextField label="Port" value={port} onChange={this.handleChange('port')} size="small" type="number" style={{flex: 1}} />
            </div>
            <div style={{display: 'flex', gap: 8}}>
              <TextField label="Username" value={username} onChange={this.handleChange('username')} size="small" style={{flex: 1}} />
              <TextField label="Password" value={password} onChange={this.handleChange('password')} size="small" style={{flex: 1}} />
            </div>
            <div style={{display: 'flex', gap: 8}}>
              <TextField label="Throughput (TPS)" value={throughput} onChange={this.handleChange('throughput')} size="small" type="number" style={{flex: 1}} helperText="0 = unlimited" />
              <TextField label="Max Pending" value={maxPending} onChange={this.handleChange('maxPending')} size="small" type="number" style={{flex: 1}} />
            </div>
            {_protocol === 'smpp' && (() => {
              const sessionsErr = this.sessionsError();
              return (
                <TextField
                  label="Sessions (SMPP binds)"
                  value={instances}
                  onChange={this.handleChange('instances')}
                  size="small"
                  type="number"
                  inputProps={{min: 1, max: 8, step: 1}}
                  error={!!sessionsErr}
                  helperText={sessionsErr || 'Parallel SMPP sessions to this SMSC on the same credentials. More than 1 requires a TPS cap.'}
                />
              );
            })()}
            {_protocol === 'smpp' && (
              <Typography variant="caption" color="text.secondary" sx={{mt: -1}}>
                {this.sessionsSummary()}
              </Typography>
            )}
            <FormControlLabel
              control={<Checkbox checked={isActive} onChange={(e) => this.setState({isActive: e.target.checked})} />}
              label="Active"
            />

            {/* SMPP Protocol Section */}
            {_protocol === 'smpp' && <>
              <Divider />
              <SectionHeader label="SMPP Protocol" expanded={showSmppSection} onClick={() => this.setState({showSmppSection: !showSmppSection})} />
              <Collapse in={showSmppSection}>
                <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                  <TextField label="Interface Version" value={interfaceVersion} onChange={this.handleNumberChange('interfaceVersion')} size="small" select>
                    {SMPP_VERSION_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                  <FormControlLabel
                    control={<Checkbox checked={transceiverMode} onChange={(e) => this.setState({transceiverMode: e.target.checked})} />}
                    label="Transceiver Mode"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{mt: -1}}>
                    When enabled, uses a single connection for both sending and receiving.
                  </Typography>
                </div>
              </Collapse>

              {/* Address Configuration */}
              <Divider />
              <SectionHeader label="Address Configuration (TON/NPI)" expanded={showAddressSection} onClick={() => this.setState({showAddressSection: !showAddressSection})} />
              <Collapse in={showAddressSection}>
                <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                  <Typography variant="caption" color="text.secondary">
                    Type of Number (TON) and Numbering Plan Indicator (NPI) for source and destination addresses.
                  </Typography>
                  <div style={{display: 'flex', gap: 8}}>
                    <TextField label="Source Addr TON" value={sourceAddrTon} onChange={this.handleNumberChange('sourceAddrTon')} size="small" select style={{flex: 1}}>
                      {TON_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                    <TextField label="Source Addr NPI" value={sourceAddrNpi} onChange={this.handleNumberChange('sourceAddrNpi')} size="small" select style={{flex: 1}}>
                      {NPI_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                  </div>
                  <div style={{display: 'flex', gap: 8}}>
                    <TextField label="Dest Addr TON" value={destAddrTon} onChange={this.handleNumberChange('destAddrTon')} size="small" select style={{flex: 1}}>
                      {TON_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                    <TextField label="Dest Addr NPI" value={destAddrNpi} onChange={this.handleNumberChange('destAddrNpi')} size="small" select style={{flex: 1}}>
                      {NPI_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                  </div>
                </div>
              </Collapse>

              {/* Connection Timers */}
              <Divider />
              <SectionHeader label="Connection Timers" expanded={showTimerSection} onClick={() => this.setState({showTimerSection: !showTimerSection})} />
              <Collapse in={showTimerSection}>
                <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                  <div style={{display: 'flex', gap: 8}}>
                    <TextField label="Reconnect Delay (sec)" value={reconnectDelay} onChange={this.handleChange('reconnectDelay')} size="small" type="number" style={{flex: 1}} helperText="Delay before reconnecting" />
                    <TextField label="Keepalive (sec)" value={keepalive} onChange={this.handleChange('keepalive')} size="small" type="number" style={{flex: 1}} helperText="TCP keepalive interval" />
                  </div>
                  <div style={{display: 'flex', gap: 8}}>
                    <TextField label="Enquire Link Interval (sec)" value={enquireLinkInterval} onChange={this.handleChange('enquireLinkInterval')} size="small" type="number" style={{flex: 1}} helperText="SMPP heartbeat interval" />
                    <TextField label="Wait ACK (sec)" value={waitAck} onChange={this.handleChange('waitAck')} size="small" type="number" style={{flex: 1}} helperText="Wait for SMSC response" />
                  </div>
                  <TextField label="Wait ACK Expire Action" value={waitAckExpire} onChange={this.handleChange('waitAckExpire')} size="small" helperText="0x00 = disconnect, 0x01 = retry, 0x02 = just log" />
                  <TextField
                    label="Message ID Type (DLR Correlation)"
                    value={msgIdType}
                    onChange={this.handleChange('msgIdType')}
                    size="small"
                    select
                    helperText="Controls hex/decimal normalization of SMSC message IDs for DLR matching"
                  >
                    <MenuItem value="">Default (string match)</MenuItem>
                    <MenuItem value="0x00">0x00 — Both decimal</MenuItem>
                    <MenuItem value="0x01">0x01 — submit_sm_resp hex, deliver_sm decimal</MenuItem>
                    <MenuItem value="0x02">0x02 — submit_sm_resp decimal, deliver_sm hex</MenuItem>
                    <MenuItem value="0x03">0x03 — Both hex</MenuItem>
                  </TextField>
                </div>
              </Collapse>
            </>}

            {/* DLT TM Chain Section */}
            <Divider sx={{my: 1}} />
            <div style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}} onClick={() => this.setState({showDltSection: !showDltSection})}>
              <Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1}}>DLT TM Chain (TLV 0x1402)</Typography>
              {showDltSection ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </div>
            <Collapse in={showDltSection}>
              <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                <TextField
                  label="DLT Telemarketer ID (TM-D)"
                  value={dltTmId}
                  onChange={this.handleChange('dltTmId')}
                  size="small"
                  placeholder="e.g. 1302159471108554813"
                  helperText="Operator's DLT TM ID. Combined with ComBird TM-A at runtime."
                />
                <TextField
                  label="TM Mode"
                  value={dltTmMode}
                  onChange={this.handleChange('dltTmMode')}
                  size="small"
                  select
                  helperText="How to send TLV 0x1402: raw chain or SHA256 hash"
                >
                  <MenuItem value="chain">Chain (raw TM IDs)</MenuItem>
                  <MenuItem value="hash">Hash (SHA256)</MenuItem>
                </TextField>
                <Typography variant="caption" color="text.secondary" sx={{mt: 1, fontWeight: 600}}>TLV Tag Overrides (optional)</Typography>
                <Typography variant="caption" color="text.secondary" sx={{mt: -1}}>Override default SMPP TLV tags. Leave empty for defaults (0x1400, 0x1401, 0x1402).</Typography>
                <div style={{display: 'flex', gap: 8}}>
                  <TextField label="Entity ID Tag" value={dltEntityTag} onChange={this.handleChange('dltEntityTag')} size="small" style={{flex: 1}} placeholder="0x1400" helperText="Default: 0x1400" />
                  <TextField label="Template ID Tag" value={dltTemplateTag} onChange={this.handleChange('dltTemplateTag')} size="small" style={{flex: 1}} placeholder="0x1401" helperText="Default: 0x1401" />
                  <TextField label="TM Chain Tag" value={dltTmTag} onChange={this.handleChange('dltTmTag')} size="small" style={{flex: 1}} placeholder="0x1402" helperText="Default: 0x1402" />
                </div>
              </div>
            </Collapse>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={this.handleSubmit} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default EditOperator;
